import React, { useEffect, useMemo, useState } from 'react'
import { CloseButton, Col, Container, Form, OverlayTrigger, Row, Spinner, Stack, Tooltip } from 'react-bootstrap'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDebounce } from 'use-debounce'
import { useCookies } from 'react-cookie'

import { useEffectiveSource } from '../../../../common/useEffectiveSource'
import { IntegrationServiceId } from '../../../../common/integrations'
import { useDocumentTitle } from '../../../../common/useDocumentTitle'
import { parseMetadata } from '../../../../common/util'

import { SessionMeetingTemplateFilter } from '../../../../components/SessionFilters/SessionMeetingTemplateFilter'
import { useSessionAvailability, useZocksAuth, useZocksQuota } from '../../../../components/ScopedBlock/hooks'
import { AllFilterModal } from '../../../../components/SessionFilters/AllFilterModal'
import { CustomPaginationComponent } from '../../../../components/DataTable/CustomPaginationComponent'
import { SessionContactFilter } from '../../../../components/SessionFilters/SessionContactFilter'
import { MultiScopedBlock, ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { SessionDateFilter } from '../../../../components/SessionFilters/SessionDateFilter'
import { SessionUserFilter } from '../../../../components/SessionFilters/SessionUserFilter'
import { useSessionFilters } from '../../../../components/SessionFilters/useSessionFilters'
import { SessionQuickFilter } from '../../../../components/SessionFilters/QuickFilters'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { AllFilterModalMode, FilterAttribute } from '../../../../components/SessionFilters/model'
import { LinkButton } from '../../../../components/Button/LinkButton'
import { Divider } from '../../../../components/Divider/Divider'
import { Button } from '../../../../components/Button/Button'
import { ZoomOBFWarningOverlay } from '../../../../components/ZoomOBFWarning/ZoomOBFWarningOverlay'
import { McpPromoBanners } from '../../../../views/Growth/McpPromo/McpPromoBanners'
import { McpPromoPage } from '../../../../views/Growth/McpPromo/mcpPromoConfig'

import { RoomSession } from '../../../../models/Room'

import NoMeetingsFoundIcon from '../../../../assets/images/no-meetings-found-icon.png'

import { useGetDefaultMeetingTemplateQuery, useGetMeetingTemplatesQuery } from '../../../../store/ai/meetingTemplate'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { useGetAccountQuery } from '../../../../store/accountUserSettings/accountUserSettings'
import { useGetMeQuery } from '../../../../store/user/user'
import { useAppSelector } from '../../../../store/hooks'
import {
  selectPastMeetingsCompositeFilter,
  selectSessionFilter,
  selectSessionFilterDateRange,
  selectSessionFilterLoading,
  selectSessionFilterMeetingKind,
  selectSessionFilterMeetingSources,
  selectSessionFilterMeetingTemplateIds,
  selectSessionFilterTags,
  selectSessionFilterUserIds,
} from '../../../../store/sessionFilter/sessionFilter'

import { MeetingParticipantsDisplay } from '../../../../views/LiveMeetings/MeetingParticipantsDisplay'
import { MeetingCreatorDisplay } from '../../../../views/LiveMeetings/MeetingCreatorDisplay'
import { MeetingTitleDisplay } from '../../../../views/LiveMeetings/MeetingTitleDisplay'
import { MeetingDateDisplay } from '../../../../views/LiveMeetings/MeetingDateDisplay'
import { PrivateBadge } from '../../../../views/LiveMeetings/PrivateBadge'

import { DeleteRoomSessionModal } from '../../DeleteRoomSessionModal'

import { useIntegrations } from '../../../../views/Integration/useIntegrations'
import { formatDateDDMMYYYY } from '../common/aifeatures/utils'
import { TrackEvent, useTracking } from '../common/tracking'
import { useUserFilter } from '../common/userFilter/hooks'

import { useGetRoomSessionsForFilterQuery } from '../store/fa'

import { MeetingDurationDisplay } from './MeetingDurationDisplay'
import { MeetingTemplateDisplay } from './MeetingTemplateDisplay'
import { MeetingScoreDisplay } from './MeetingScoreDisplay'
import { NotStartedBadge } from './NotStartedBadge'
import { CreatorIcon } from './CreatorIcon'
import { SourceIcon } from './SourceIcon'

import './PastMeetings.scss'

const SPEAKER_DISPLAY_THRESHOLD = 0.005
const HIDE_PHONE_INVITE_CTA_COOKIE_MAX_AGE_THREE_MONTH = 60 * 60 * 24 * 30 * 3
const _SEARCH_TERM_MIN_LENGTH = 3
const _SEARCH_TERM_PARAM_KEY = 'term'

export const PastMeetings = () => {
  useDocumentTitle('Past Meetings')

  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)

  const compositeFilter = useAppSelector(selectPastMeetingsCompositeFilter)
  const sessionQuickFilter = useAppSelector(selectSessionFilter)
  const sessionQuickFilterIsLoading = useAppSelector(selectSessionFilterLoading)
  const sessionFilterUserIds = useAppSelector(selectSessionFilterUserIds)
  const sessionFilterDateRange = useAppSelector(selectSessionFilterDateRange)
  const sessionFilterMeetingTypeIds = useAppSelector(selectSessionFilterMeetingTemplateIds)
  const sessionFilterMeetingKind = useAppSelector(selectSessionFilterMeetingKind)
  const sessionFilterMeetingSources = useAppSelector(selectSessionFilterMeetingSources)
  const sessionFilterTags = useAppSelector(selectSessionFilterTags)

  const filterCount =
    sessionFilterUserIds.length +
    (sessionFilterDateRange ? 1 : 0) +
    sessionFilterMeetingTypeIds.length +
    sessionFilterTags.length +
    (sessionFilterMeetingKind ? 1 : 0) +
    sessionFilterMeetingSources.length

  const [showAllFilterModal, setShowAllFilterModal] = useState<boolean>(false)
  const [allFilterModalMode, setAllFilterModalMode] = useState<AllFilterModalMode>(AllFilterModalMode.VIEW)
  const [searchTerm, setSearchTerm] = useState<string>(searchParams.get(_SEARCH_TERM_PARAM_KEY) ?? '')

  const [debouncedSearchTerm] = useDebounce(searchTerm, 400)
  const activeSearchTerm = debouncedSearchTerm.length >= _SEARCH_TERM_MIN_LENGTH ? debouncedSearchTerm : null

  const defaultUserIds = useMemo(() => [currentUserId], [currentUserId])

  const { page, pageSize, totalElements, setTotalElements, handlePageChange, handlePageSizeChange, handleFilterChange, resetFilters } = useSessionFilters({
    defaultUserIds,
    currentUserId,
  })

  const { available: sessionAvailable, isSuccess: sessionAvailabilitySuccess } = useSessionAvailability()

  const { data: roomSessions, isFetching: roomSessionsLoading } = useGetRoomSessionsForFilterQuery(
    {
      accountId,
      ...(sessionQuickFilter ? { filterId: sessionQuickFilter.id } : { filter: compositeFilter }),
      ...(activeSearchTerm ? { term: activeSearchTerm } : {}),
      sort: { attr: FilterAttribute.STARTED, dir: 'desc' },
      pageRequest: {
        page: page - 1,
        size: pageSize,
      },
    },
    {
      skip: sessionQuickFilterIsLoading,
    },
  )

  useEffect(() => {
    setTotalElements(roomSessions?.page.totalElements ?? 0)
  }, [roomSessions, setTotalElements])

  const onImportButtonClicked = tracked(
    () => {
      navigate(`/workspace/past-meetings/import`)
    },
    { eventName: TrackEvent.UPLOAD_RECORDING_CLICKED },
  )

  const atRoomSessionLimitActions = () => {
    return sessionAvailabilitySuccess && sessionAvailable
  }

  const onHideAllFilterModal = () => {
    setShowAllFilterModal(false)
    setAllFilterModalMode(AllFilterModalMode.VIEW)
  }

  const handleOnEditClicked = () => {
    setAllFilterModalMode(AllFilterModalMode.EDIT)
    setShowAllFilterModal(true)
  }

  const handleNewFilterClicked = () => {
    resetFilters()
    setAllFilterModalMode(AllFilterModalMode.NEW)
    setShowAllFilterModal(true)
  }

  const handleSearchTermChange = (searchTerm: string) => {
    const newSearchParams = new URLSearchParams(searchParams)
    if (!searchTerm) {
      newSearchParams.delete(_SEARCH_TERM_PARAM_KEY)
    } else {
      newSearchParams.set(_SEARCH_TERM_PARAM_KEY, searchTerm)
    }

    setSearchTerm(searchTerm)
    setSearchParams(newSearchParams)
  }

  return (
    <Container>
      {showAllFilterModal ? (
        <AllFilterModal
          show={showAllFilterModal}
          onHide={onHideAllFilterModal}
          mode={allFilterModalMode}
          filterType='past'
          onFilterChanged={handleFilterChange}
        />
      ) : null}
      <Stack gap={5}>
        <Row>
          <Col className='p-0'>
            <h3>Past meetings</h3>
          </Col>
        </Row>
        <ZoomOBFWarningOverlay />
        <McpPromoBanners page={McpPromoPage.PAST_MEETINGS} />
        <Row>
          <ScopedBlock
            scope={{
              scope: 'room_session:import',
              superScope: ['room_session:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <ScopedBlock authActions={[atRoomSessionLimitActions]} missingScopeAction={MissingScopeAction.DISABLE}>
              <Button className='past-meetings__upload-recording' variant='primaryGhost' onClick={onImportButtonClicked}>
                <i className='bi bi-plus-circle' />
                Upload Meeting
              </Button>
            </ScopedBlock>
          </ScopedBlock>
        </Row>
        <Stack gap={4}>
          <Row className='align-items-end gap-1 m-0'>
            <Col className='d-none d-lg-block p-0'>
              <SessionUserFilter filterType='past' disabled={roomSessionsLoading} onFilterChanged={handleFilterChange} />
            </Col>
            <Col className='d-none d-lg-block p-0'>
              <SessionContactFilter filterType='past' disabled={roomSessionsLoading} onFilterChanged={handleFilterChange} />
            </Col>
            <Col className='d-none d-lg-block p-0'>
              <SessionDateFilter filterType='past' disabled={roomSessionsLoading} onFilterChanged={handleFilterChange} />
            </Col>
            <Col className='d-none d-lg-block p-0'>
              <SessionMeetingTemplateFilter filterType='past' disabled={roomSessionsLoading} onFilterChanged={handleFilterChange} />
            </Col>
            <Col xs={12} sm className='mw-0 p-0'>
              <SessionQuickFilter
                disabled={roomSessionsLoading}
                onEditClicked={handleOnEditClicked}
                onNewFilterClicked={handleNewFilterClicked}
                filterType='past'
                onFilterChanged={handleFilterChange}
              />
            </Col>

            <Col xs={12} sm='auto' lg className='mt-3 mt-sm-0 p-0'>
              <div className='d-flex zocks-gap-0.75 align-items-center justify-content-end'>
                <div className='d-none d-lg-flex gap-1 cursor-pointer'>
                  <i className='bi bi-arrow-clockwise' />
                  <Button variant='link' className='button-link-medium' onClick={resetFilters}>
                    Clear all
                  </Button>
                </div>
                <Button variant='darkGhost' className='d-none d-sm-flex p-2 paragraph-medium' onClick={() => setShowAllFilterModal(true)}>
                  <i className='bi bi-funnel' />
                  <div className='d-lg-none paragraph-small'>All filters</div>
                  <div className='lead-small'>{filterCount}</div>
                </Button>
                <Button variant='darkGhost' className='d-flex d-sm-none p-2 w-100 paragraph-medium' onClick={() => setShowAllFilterModal(true)}>
                  <i className='bi bi-funnel' />
                  <div className='paragraph-small'>All filters</div>
                  <div className='lead-small'>{filterCount}</div>
                </Button>
              </div>
            </Col>
          </Row>
          <Divider />
          <Row>
            <Col>
              <div>
                {roomSessions?.data.length} of {roomSessions?.page.totalElements} {`meeting${(roomSessions?.page.totalElements ?? 0) !== 1 ? 's' : ''}`}
              </div>
            </Col>
            <Col xs={12} md='auto' className='m-0'>
              <div className='d-flex align-items-center zocks-gap-1.25 border rounded-1 zocks-border-color-gray-500 zocks-px-0.75 past-meetings-search-bar-wrapper'>
                <Form.Control
                  type='text'
                  value={searchTerm}
                  onChange={(e) => handleSearchTermChange(e.currentTarget.value)}
                  placeholder='Search in meetings'
                  className='border-0 px-0'
                />
                <i className='bi bi-search' />
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              {roomSessionsLoading || !roomSessions ? (
                <div className='d-flex justify-content-center'>
                  <Spinner />
                </div>
              ) : (
                <>
                  <PhoneInviteCta />
                  {roomSessions.data.length ? (
                    <>
                      <RoomSessionList roomSessions={roomSessions.data} showDemoSession={false} />
                      <Container fluid>
                        <Row className='justify-content-end'>
                          <Col>
                            <CustomPaginationComponent
                              rowsPerPage={pageSize}
                              rowCount={totalElements}
                              currentPage={page}
                              onChangePage={handlePageChange}
                              onChangeRowsPerPage={handlePageSizeChange}
                            />
                          </Col>
                        </Row>
                      </Container>
                    </>
                  ) : (
                    <NoRoomSessionsFound showClearButton={searchTerm.length > 0} onClear={() => handleSearchTermChange('')} />
                  )}
                </>
              )}
            </Col>
          </Row>
        </Stack>
      </Stack>
    </Container>
  )
}

export const RoomSessionList: React.FC<{
  roomSessions: RoomSession[]
  showDemoSession: boolean
  onSessionDeleted?: (session: RoomSession) => void
}> = ({ roomSessions, showDemoSession, onSessionDeleted }) => {
  const onSessionDeletedAction = (session: RoomSession) => {
    onSessionDeleted?.(session)
  }

  const groupedRoomSessions = roomSessions.reduce((acc: { [key: string]: RoomSession[] }, roomSession) => {
    const date = formatDateDDMMYYYY(roomSession.startedAt)
    acc[date] = acc[date] || []
    acc[date].push(roomSession)
    return acc
  }, {})

  const today = formatDateDDMMYYYY(new Date().toDateString())

  return (
    <Container fluid className='p-0'>
      {showDemoSession && (
        <Row>
          <Col>You don't have any meetings yet.</Col>
        </Row>
      )}
      {Object.entries(groupedRoomSessions).map(([date, roomSessions], idx) => {
        return (
          <div className='zocks-mb-1.5' key={`session_${idx}`}>
            <div className='lead-medium'>{date === today ? 'Today' : date}</div>
            <Stack gap={2} className='p-3'>
              {roomSessions.map((roomSession) => {
                return <RoomSessionListItem session={roomSession} key={roomSession.id} onSessionDeleted={onSessionDeletedAction} />
              })}
            </Stack>
          </div>
        )
      })}
    </Container>
  )
}

export const PhoneInviteCta = () => {
  const [cookies, setCookie] = useCookies(['hidePhoneInviteCta'])
  const accountId = useAppSelector(selectAccountId)
  const { data: userData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const { hasScope } = useZocksAuth()
  const { isIntegrationAllowed } = useIntegrations()
  const hasStartSessionScope = hasScope('**', 'room_session:start', ['room_session:admin', 'room:admin'])

  const hasPhoneIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.serviceId === IntegrationServiceId.PHONE && i.state === 'live'))
  }, [userData])
  const handlePhoneInviteCTAClose = () => {
    setCookie('hidePhoneInviteCta', true, { path: '/', maxAge: HIDE_PHONE_INVITE_CTA_COOKIE_MAX_AGE_THREE_MONTH })
  }

  if (!hasStartSessionScope || cookies.hidePhoneInviteCta || hasPhoneIntegration || !isIntegrationAllowed(IntegrationServiceId.PHONE)) {
    return null
  }
  return (
    <Container
      fluid
      className='phone-invite-cta-container p-3 zocks-mb-2.5 mb-md-0 align-items-center border zocks-border-color-primary zocks-border-0.25 position-relative'
    >
      <CloseButton className='position-absolute top-0 end-0 m-2' onClick={handlePhoneInviteCTAClose} />
      <Row>
        <Col xs={12} sm={12} md={8}>
          <Container fluid className='p-0 d-flex align-items-center'>
            <Row>
              <Col xs='auto'>
                <SourceIcon source='phone' />
              </Col>
              <Col className='d-flex flex-column justify-content-center overflow-hidden'>
                <Row>
                  <Col>
                    <h6>Invite Zocks to your Phone Calls</h6>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <div>Zocks can be used to analyze and summarize conversations from mobile and landline phone calls.</div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>
        </Col>
        <Col xs={12} sm={12} md={true} className='py-3 p-md-0 d-flex justify-content-center align-items-center'>
          <div className='view-meeting-btn'>
            <LinkButton variant='primary' to='/account/profile-settings/integrations'>
              Link your phone number
            </LinkButton>
          </div>
        </Col>
      </Row>
    </Container>
  )
}

export const RoomSessionListItem: React.FC<{
  session: RoomSession
  onClick?: (session: RoomSession) => void
  onSessionDeleted?: (session: RoomSession) => void
  hideDeleteButton?: boolean
}> = ({ session, onClick, onSessionDeleted, hideDeleteButton = false }) => {
  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const { data: meetingTemplates } = useGetMeetingTemplatesQuery(
    { accountId, pageRequest: { page: 0, size: 500 }, includeInactive: true },
    { refetchOnMountOrArgChange: true },
  )
  const { data: defaultMeetingTemplate } = useGetDefaultMeetingTemplateQuery(
    {
      accountId,
      userId: currentUserId,
    },
    { refetchOnMountOrArgChange: true },
  )
  const { data: account } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const {
    source,
    creator_service_id: creatorServiceId,
    participantSpeakingDistribution: speakingDistribution = {},
    zocksProcessing: zocksProcessingValue,
    provider = null,
    external: { meetingTemplateId = null, title = null, platform = null, userAgent = null } = {},
  } = parseMetadata(session.metadata)

  const effectiveSource = useEffectiveSource(source, platform, userAgent, provider)

  const [deleteModalShow, setDeleteModalShow] = useState(false)
  const { hasQuota } = useZocksQuota()
  const restrictedSessionDeleteSettingAvailable = hasQuota('room.restrictedSessionDelete.value')
  const onSessionDelete = () => {
    setDeleteModalShow(true)
  }

  const handleOnSessionDeleted = () => {
    setDeleteModalShow(false)
    onSessionDeleted?.(session)
  }

  const meetingTemplate = useMemo(() => {
    if (!meetingTemplates) {
      return null
    }

    const sessionMeetingTemplate = meetingTemplateId ? meetingTemplates.data.find((meetingTemplate) => meetingTemplate.id === meetingTemplateId) : null
    const userDefaultMeetingTemplate = defaultMeetingTemplate ?? meetingTemplates.data.find((meetingTemplate) => meetingTemplate.isDefault)
    return sessionMeetingTemplate ?? userDefaultMeetingTemplate ?? null
  }, [meetingTemplates, meetingTemplateId, defaultMeetingTemplate])
  const { userFilterValues } = useUserFilter('past')

  return (
    <Container fluid className='meeting-card'>
      <DeleteRoomSessionModal roomSession={session} show={deleteModalShow} onSessionDeleted={handleOnSessionDeleted} onHide={() => setDeleteModalShow(false)} />
      <Row className='m-0'>
        <Col xs={12} lg className='px-0 meeting-info'>
          <Row className='d-flex gap-3 m-0'>
            <Col xs='auto' className='px-0'>
              <SourceIcon source={effectiveSource ?? source} />
            </Col>
            <Col className='d-flex flex-column justify-content-center overflow-hidden px-0 meeting-details'>
              <Row className='meeting-status-row m-0 align-items-center'>
                <Col xs='auto' className='p-0'>
                  {creatorServiceId && <CreatorIcon creator={creatorServiceId} />}
                </Col>
                <Col xs='auto' className='p-0'>
                  <MeetingDateDisplay startedAt={session.startedAt} finishedAt={session.finishedAt} />
                </Col>
                {session.isPrivate ? (
                  <Col xs='auto' className='p-0'>
                    <PrivateBadge />
                  </Col>
                ) : null}
                <Col xs='auto' className='p-0'>
                  <NotStartedBadge roomSession={session} />
                </Col>
              </Row>
              <Row>
                <Col>
                  <MeetingTitleDisplay title={title} />
                </Col>
              </Row>
              <Row>
                <Col xs='auto' className='paragraph-medium medium upcoming-created-by-name'>
                  <Stack direction='horizontal' gap={3}>
                    <MeetingCreatorDisplay creatorId={session.roomCreatedBy?.id} userLabels={userFilterValues} />
                    <MeetingDurationDisplay startedAt={session.startedAt} finishedAt={session.finishedAt} />
                    <MeetingParticipantsDisplay
                      creatorId={session.roomCreatedBy?.id}
                      users={session.users.filter(
                        (user) => (speakingDistribution[user.id] ?? 0) > SPEAKER_DISPLAY_THRESHOLD || !user.id.startsWith('speaker_'),
                      )}
                      countOnly={true}
                    />
                    <MeetingTemplateDisplay meetingTemplate={meetingTemplate} />
                    <MeetingScoreDisplay meetingTemplate={meetingTemplate} roomSession={session} />
                  </Stack>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>

        <Col xs={12} sm={true} lg={'auto'} className='gap-3 py-3 p-md-0 align-content-center'>
          <Stack direction='horizontal' gap={3}>
            <div>
              {!hideDeleteButton &&
                account &&
                (!account.data.effectiveConfig.restrictedSessionDelete || !restrictedSessionDeleteSettingAvailable ? (
                  <ScopedBlock
                    missingScopeAction={MissingScopeAction.DISABLE}
                    scope={{ scope: 'room_session:delete', superScope: ['room:admin', 'room_session:admin'] }}
                  >
                    <OverlayTrigger
                      trigger={zocksProcessingValue ? ['hover', 'focus'] : []}
                      overlay={
                        <Tooltip id='default-mt-tooltip'>Meeting cannot be deleted while processing is in progress. It usually requires a few minutes.</Tooltip>
                      }
                    >
                      <Button variant='primaryGhost' className='zocks-p-0.75' onClick={onSessionDelete} disabled={session.isActive || !!zocksProcessingValue}>
                        <i className='bi bi-trash' />
                      </Button>
                    </OverlayTrigger>
                  </ScopedBlock>
                ) : (
                  <OverlayTrigger
                    trigger={zocksProcessingValue ? ['hover', 'focus'] : []}
                    overlay={
                      <Tooltip id='default-mt-tooltip'>Meeting cannot be deleted until processing is in progress. It usually requires a few minutes.</Tooltip>
                    }
                  >
                    <MultiScopedBlock
                      scopedBlocks={[
                        {
                          scope: { scope: 'room_session:delete', superScope: ['room:admin', 'room_session:admin'] },
                          missingScopeAction: MissingScopeAction.DISABLE,
                        },
                        {
                          scope: { scope: 'room_session:admin_delete', superScope: [] },
                          missingScopeAction: MissingScopeAction.DISABLE,
                        },
                      ]}
                    >
                      <Button
                        variant='primaryGhost'
                        className='zocks-p-0.75'
                        onClick={onSessionDelete}
                        disabled={session.isActive || !!zocksProcessingValue || account.data.effectiveConfig.restrictedSessionDeleteGlobal}
                      >
                        <i className='bi bi-trash' />
                      </Button>
                    </MultiScopedBlock>
                  </OverlayTrigger>
                ))}
            </div>
            <div className='view-meeting-btn'>
              <LinkButton variant='lightPurple' to={`/workspace/past-meetings/room/${session.roomId}/session/${session.id}`} onClick={() => onClick?.(session)}>
                View
              </LinkButton>
            </div>
          </Stack>
        </Col>
      </Row>
    </Container>
  )
}

interface NoRoomSessionsFoundProps {
  showClearButton: boolean
  onClear: () => void
}

const NoRoomSessionsFound: React.FC<NoRoomSessionsFoundProps> = ({ showClearButton, onClear }) => {
  return (
    <div className='d-flex flex-column align-items-center gap-3 zocks-py-2.0 w-100 past-meetings-no-room-sessions-found-container'>
      <img alt='No meetings found' src={NoMeetingsFoundIcon} />
      <div className='text-center'>
        <h5>No meetings found</h5>
        <span className='paragraph-medium zocks-text-gray-600'>Try different search terms.</span>
      </div>
      {showClearButton ? (
        <Button variant='grayGhost' className='button-small px-2 py-1' onClick={onClear}>
          Clear search
        </Button>
      ) : null}
    </div>
  )
}
